// store/index.js
import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    token: localStorage.getItem('token'),
    username: localStorage.getItem('username'),
    menuTree: [],
    // 全局仓库选择器
    currentWarehouseId: Number(localStorage.getItem('currentWarehouseId')) || null,
    warehouses: [],
    permissions: JSON.parse(localStorage.getItem('permissions') || '[]'),
    roles: JSON.parse(localStorage.getItem('roles') || '[]'),
    // A-1.2：连接模式（ONLY_WMS/WMS_MES/WMS_MES_ERP）；erpType 保留兼容（过渡期双字段）
    sysConnection: localStorage.getItem('sysConnection') || 'ONLY_WMS',
    // 当前工厂 ERP 连接类型（旧字段，Q3 过渡期保留）：NONE / HUAWAY / OTHER
    erpType: localStorage.getItem('erpType') || 'NONE',
    venusEnabled: localStorage.getItem('venusEnabled') !== 'false', // 任务A：维纳斯件功能开关（缺省开启，登录/切工厂后刷新）
    currentFactory: localStorage.getItem('currentFactory') || '',
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      localStorage.setItem('token', token)
    },
    SET_USERNAME(state, username) {
      state.username = username
      localStorage.setItem('username', username)
    },
    SET_MENU_TREE(state, tree) {
      state.menuTree = tree
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions
      localStorage.setItem('permissions', JSON.stringify(permissions))
    },
    SET_ROLES(state, roles) {
      state.roles = roles
      localStorage.setItem('roles', JSON.stringify(roles))
    },
    SET_VENUS_ENABLED(state, v) {
      state.venusEnabled = v === false ? false : v !== 'false'
      localStorage.setItem('venusEnabled', state.venusEnabled ? 'true' : 'false')
    },
    SET_ERP_TYPE(state, erpType) {
      state.erpType = erpType || 'NONE'
      localStorage.setItem('erpType', erpType || 'NONE')
    },
    SET_SYS_CONNECTION(state, sysConnection) {
      state.sysConnection = sysConnection || 'ONLY_WMS'
      localStorage.setItem('sysConnection', sysConnection || 'ONLY_WMS')
    },
    SET_CURRENT_FACTORY(state, factory) {
      state.currentFactory = factory || ''
      localStorage.setItem('currentFactory', factory || '')
    },
    CLEAR_AUTH(state) {
      state.token = null
      state.username = null
      state.menuTree = []
      state.currentWarehouseId = null
      state.warehouses = []
      state.permissions = []
      state.roles = []
      state.erpType = 'NONE'
      state.sysConnection = 'ONLY_WMS'
      state.currentFactory = ''
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      localStorage.removeItem('currentWarehouseId')
      localStorage.removeItem('permissions')
      localStorage.removeItem('roles')
      localStorage.removeItem('erpType')
      localStorage.removeItem('sysConnection')
      localStorage.removeItem('currentFactory')
    },
    SET_CURRENT_WAREHOUSE(state, id) {
      state.currentWarehouseId = id
      if (id) {
        localStorage.setItem('currentWarehouseId', String(id))
      } else {
        localStorage.removeItem('currentWarehouseId')
      }
    },
    SET_WAREHOUSES(state, list) {
      state.warehouses = list
    }
  },
  actions: {
    // 使用动态 import 避免顶层循环依赖
    async loadMenuTree({ commit }) {
      const { getMenuTree } = await import('@/api/menu')
      const tree = await getMenuTree()
      commit('SET_MENU_TREE', tree)
      return tree
    },
    async fetchErpType({ commit }) {
      try {
        const request = (await import('@/utils/request')).default
        const res = await request({ url: '/api/wms/config/erp-type', method: 'get' })
        // A-1.2：后端返回 { sysConnection, erpType } 双字段（兼容旧字符串返回）
        if (res && typeof res === 'object') {
          commit('SET_SYS_CONNECTION', res.sysConnection || 'ONLY_WMS')
          commit('SET_ERP_TYPE', res.erpType || 'NONE')
        } else {
          commit('SET_SYS_CONNECTION', 'ONLY_WMS')
          commit('SET_ERP_TYPE', res || 'NONE')
        }
      } catch (e) {
        commit('SET_SYS_CONNECTION', 'ONLY_WMS')
        commit('SET_ERP_TYPE', 'NONE')
      }
    },
    async fetchVenusEnabled({ commit }) {
      try {
        const request = (await import('@/utils/request')).default
        const res = await request({ url: '/api/wms/config/venus-enabled', method: 'get' })
        commit('SET_VENUS_ENABLED', res === false ? false : true)
      } catch (e) {
        commit('SET_VENUS_ENABLED', true)
      }
    },
    async fetchWarehouses({ commit }) {
      try {
        const { getWarehouseList } = await import('@/api/warehouse')
        const res = await getWarehouseList()
        const list = res || []
        commit('SET_WAREHOUSES', list)
        // 优先使用默认仓库，其次 localStorage 保存值，最后第一个
        const defaultId = list.find(w => w.defaultWarehouse)?.id
        const savedId = Number(localStorage.getItem('currentWarehouseId'))
        if (defaultId) {
          commit('SET_CURRENT_WAREHOUSE', defaultId)
        } else if (savedId && list.some(w => w.id === savedId)) {
          commit('SET_CURRENT_WAREHOUSE', savedId)
        } else {
          commit('SET_CURRENT_WAREHOUSE', list.length ? list[0].id : null)
        }
      } catch (e) {
        console.error('获取仓库列表失败', e)
      }
    },
    logout({ commit }) {
      commit('CLEAR_AUTH')
    }
  },
  getters: {
    currentWarehouseId: state => state.currentWarehouseId,
    warehouses: state => state.warehouses,
    hasPermission: (state) => (code) => {
      return state.permissions && state.permissions.includes(code)
    },
    roles: (state) => state.roles,
    erpType: (state) => state.erpType,
    sysConnection: (state) => state.sysConnection,
    venusEnabled: (state) => state.venusEnabled,
    currentFactory: (state) => state.currentFactory,
  }
})
